import Vue from "vue";

const EMPTY_COMMERCE_CONFIG = {
  enabled: false,
  currency: "gbp",
  digitalProducts: [],
  printProducts: [],
  deliveryOptions: [],
  termsUrl: "",
  digitalPolicyNotes: [],
  customerPolicyNotes: [],
};

const LAFAYETTE_COMMERCE_CONFIG = {
  enabled: false,
  currency: "gbp",
  digitalProducts: [
    { code: "digital_single", label: "Single digital image", amount: 1600, minItems: 1, maxItems: 1 },
    { code: "digital_up_to_5", label: "Up to 5 digital images", amount: 4500, minItems: 2, maxItems: 5 },
    { code: "digital_6_plus", label: "6 or more digital images", amount: 7500, minItems: 6, maxItems: null },
  ],
  printProducts: [
    {
      code: "black_folder_8x6",
      label: '8"x6" photograph in black slip-in folder',
      amount: 1400,
      description: 'One 8"x6" print of the selected image supplied in a black slip-in folder.',
    },
    {
      code: "embossed_mount_8x6",
      label: 'Events 8"x6" print in embossed mount',
      amount: 1600,
      description: 'One 8"x6" print of the selected image supplied in an embossed event mount.',
    },
    {
      code: "embossed_mount_10x8",
      label: 'Events 10"x8" print in embossed mount',
      amount: 1800,
      description: 'One 10"x8" print of the selected image supplied in an embossed event mount.',
    },
    {
      code: "pewter_pack",
      label: 'Pewter Pack - one 8"x6" plus two 5"x4" mounted prints',
      amount: 3300,
      description: 'One 8"x6" print of the selected image in a Cream Premium Strut Mount, plus two 5"x4" copies in Cream Plain Strut Mounts.',
    },
  ],
  deliveryOptions: [
    {
      code: "uk_delivery",
      label: "UK delivery",
      type: "shipping",
      amount: 1000,
      enabled: true,
      requiresAddress: true,
      allowedCountries: ["GB"],
    },
    {
      code: "international_delivery",
      label: "International delivery",
      type: "shipping",
      amount: 2000,
      enabled: true,
      requiresAddress: true,
      allowedCountries: ["AT", "AU", "BE", "CA", "CH", "CN", "DE", "DK", "ES", "FI", "FR", "HK", "IE", "IN", "IT", "JP", "MY", "NL", "NO", "NZ", "PT", "SE", "SG", "US"],
    },
  ],
  termsUrl: "https://www.lafayettephotography.com/policy/terms",
  digitalPolicyNotes: [
    "Digital image purchases are supplied as instant downloads and are generally non-refundable once access has been provided.",
    "Refunds may be considered at our discretion, without affecting your statutory rights if digital content is faulty, not as described, or not supplied.",
  ],
  customerPolicyNotes: [
    "UK postage and delivery is £10. International delivery is £20.",
    "Refunds and cancellations for print products are handled under Lafayette Photography's terms.",
    "Personalised photographic products may be excluded from cancellation rights once photographs have been taken.",
    "Where a refund is due, it is based on the amount actually paid, including any promotional discount.",
    "Where a refund is due after despatch, standard delivery may be refunded but return postage is not.",
    "Refunds are normally returned to the original payment method after returned products or return evidence have been received.",
  ],
};

const DEFAULT_CONFIG = {
  requiresConsent: false,
  consentUrl: "",
  consentEmail: "",
  consentStorageKey: "",
  consentTitle: "",
  consentBody: "",
  consentCheckboxText: "",
  isFreeDigital: false,
  isHeadshot: false,
  galleryMode: "snapshot",
  printsEnabled: true,
  faceSearchRecommended: true,
  consentMode: "none",
  ceremonyLabelOnly: false,
  eventTerminology: false,
  pricingTier: "uk",
  rememberMeEnabled: true,
  saveSelfieEnabled: false,
  rememberMeTtlDays: 30,
  newImagesTtlDays: 7,
  saveSelfieConsentVersion: "v1",
  saveSelfieConsentPolicyUrl: "/privacy-policy",
  saveSelfieConsentOrgName: "MyGraduationClip",
  commerce: EMPTY_COMMERCE_CONFIG,
};

const CONFIG_BY_SLUG = {
  ucd: {
    requiresConsent: true,
    consentUrl:
      "https://www.ucd.ie/alumni/development-and-alumni-relations-privacy-policy/",
    consentEmail: "alumni@ucd.ie",
    consentStorageKey: "consent:ucd-headshots:v1",
    isFreeDigital: true,
    isHeadshot: true,
    galleryMode: "headshot",
    printsEnabled: false,
    consentMode: "ucd_daro",
    ceremonyLabelOnly: true,
  },
  "ucd-test": {
    requiresConsent: true,
    consentUrl:
      "https://www.ucd.ie/alumni/development-and-alumni-relations-privacy-policy/",
    consentEmail: "alumni@ucd.ie",
    consentStorageKey: "consent:ucd-headshots:v1",
    isFreeDigital: true,
    isHeadshot: true,
    galleryMode: "headshot",
    printsEnabled: false,
    consentMode: "ucd_daro",
    ceremonyLabelOnly: true,
  },
  adelaide: {
    pricingTier: "aud",
  },
  "torrens-test": {
    pricingTier: "aud",
  },
  "gfpevents-test-uni": {
    pricingTier: "aud",
    saveSelfieEnabled: true,
    saveSelfieConsentOrgName: "GFP Events",
  },
  lafayette: {
    eventTerminology: true,
    commerce: LAFAYETTE_COMMERCE_CONFIG,
  },
};

export const snapshotConfigState = Vue.observable({
  bySlug: {},
});

export const setRuntimeSnapshotConfig = (slug, config) => {
  const key = String(slug || "").toLowerCase();
  if (!key || !config) return;
  Vue.set(snapshotConfigState.bySlug, key, config);
};

export const getSnapshotConfig = (slug) => {
  const key = String(slug || "").toLowerCase();
  const base = {
    ...DEFAULT_CONFIG,
    consentStorageKey: key ? `consent:${key}:v1` : "",
  };
  const config = {
    ...base,
    ...(CONFIG_BY_SLUG[key] || {}),
    ...(snapshotConfigState.bySlug[key] || {}),
  };
  config.commerce = {
    ...base.commerce,
    ...((CONFIG_BY_SLUG[key] || {}).commerce || {}),
    ...((snapshotConfigState.bySlug[key] || {}).commerce || {}),
  };
  config.commerce.deliveryOptions = Array.isArray(config.commerce.deliveryOptions)
    ? config.commerce.deliveryOptions
    : base.commerce.deliveryOptions;
  config.commerce.digitalProducts = Array.isArray(config.commerce.digitalProducts)
    ? config.commerce.digitalProducts
    : base.commerce.digitalProducts;
  config.commerce.printProducts = Array.isArray(config.commerce.printProducts)
    ? config.commerce.printProducts
    : base.commerce.printProducts;
  if (!config.galleryMode || config.galleryMode === "snapshot") {
    config.galleryMode = config.isHeadshot ? "headshot" : "snapshot";
  }
  config.isHeadshot = config.galleryMode === "headshot";
  return config;
};
